// Shared base for the slaved chart strips — battery, signal, attitude. They all
// draw traces over the flight, hang off the profile's playhead, let each series
// be switched on or off, and show a vertical key whose values track the slider.
// What differs is which series they carry and how each is scaled, so a subclass
// supplies series(), the axis details live on the series specs, and everything
// else — the toggle, the key, the live values, seeking and the marker — is here.

const SEVERITY_COLOUR = {
  critical: 'var(--warn)',
  warning: 'var(--caution)',
  info: 'var(--trace-deep)',
};

const WRAP_GAP = 180; // a jump larger than this (yaw) is the heading seam

export class Strip {
  constructor(container, flight, onSeek) {
    this.container = container;
    this.flight = flight;
    this.onSeek = onSeek;
    this.index = 0;
    this.render();
  }

  // ---- subclass hooks -------------------------------------------------------

  /** localStorage key for which series are shown. */
  storageKey() { return 'ddlv-strip'; }
  /** Series specs: { id, key, label, colour, opacity?, dash?, min?, max?, sym?, wrap?, fmt? }. */
  series() { return []; }
  ariaLabel() { return 'Timeline'; }
  /** Rows to pip, as { index, severity }. Default: none. */
  pipRows() { return []; }
  /** Extra content for the left column (e.g. battery pack buttons). */
  leftExtras() { return ''; }
  bindExtras() {}
  /** Minimum half-span for a symmetric axis, so level flight is not over-zoomed. */
  symFloor() { return 10; }

  // ---- visibility -----------------------------------------------------------

  allIds() { return this.series().map((s) => s.id); }

  visible() {
    try {
      const stored = localStorage.getItem(this.storageKey());
      if (stored) return new Set(JSON.parse(stored));
    } catch { /* storage may be unavailable */ }
    return new Set(this.allIds());
  }

  applyVisibility() {
    const shown = this.visible();
    for (const node of this.container.querySelectorAll('[data-series]')) {
      node.classList.toggle('series-off', !shown.has(node.dataset.series));
    }
    for (const button of this.container.querySelectorAll('[data-toggle]')) {
      const on = shown.has(button.dataset.toggle);
      button.setAttribute('aria-pressed', String(on));
      button.classList.toggle('off', !on);
    }
  }

  toggleSeries(id) {
    const shown = this.visible();
    if (shown.has(id)) shown.delete(id); else shown.add(id);
    try { localStorage.setItem(this.storageKey(), JSON.stringify([...shown])); } catch { /* preference only */ }
    this.applyVisibility();
  }

  // ---- scaling helpers ------------------------------------------------------

  lowest(values) {
    let low = null;
    for (const v of values) if (v !== null && (low === null || v < low)) low = v;
    return low === null ? 0 : low;
  }

  highest(values) {
    let high = null;
    for (const v of values) if (v !== null && (high === null || v > high)) high = v;
    return high === null ? 1 : high;
  }

  symSpan() {
    let m = 0;
    for (const spec of this.series()) {
      if (!spec.sym) continue;
      const values = this.flight.cols[spec.key];
      if (!values) continue;
      for (const v of values) if (v !== null && Math.abs(v) > m) m = Math.abs(v);
    }
    return Math.max(this.symFloor(), Math.ceil(m));
  }

  buildY(spec, geom, sym) {
    const { padTop, plotHeight } = geom;
    if (spec.sym) {
      const mid = padTop + plotHeight / 2;
      return (v) => mid - (v / sym) * (plotHeight / 2);
    }
    const values = this.flight.cols[spec.key];
    const low = spec.min !== undefined ? spec.min : this.lowest(values);
    const high = spec.max !== undefined ? spec.max : this.highest(values);
    const span = (high - low) || 1;
    return (v) => padTop + plotHeight - ((v - low) / span) * plotHeight;
  }

  // ---- render ---------------------------------------------------------------

  fmtVal(spec, v) {
    if (v === null || v === undefined) return '—';
    return spec.fmt ? spec.fmt(v) : String(v);
  }

  render() {
    const width = 1000;
    const height = 60;
    const padTop = 7;
    const padBottom = 12;
    const plotHeight = height - padTop - padBottom;
    const geom = { padTop, plotHeight };

    const { timeS } = this.flight.cols;
    const count = this.flight.count;
    const duration = timeS[count - 1] || 1;
    const x = (i) => (timeS[i] / duration) * width;
    const step = Math.max(1, Math.ceil(count / 250));
    const sym = this.symSpan();

    const specs = this.series().filter((s) => this.flight.has(s.key));

    const paths = specs
      .map((spec) => {
        const y = this.buildY(spec, geom, sym);
        const values = this.flight.cols[spec.key];
        let d = '';
        let prev = null;
        for (let i = 0; i < count; i += step) {
          const value = values[i];
          if (value === null) { prev = null; continue; }
          const lift = prev === null || (spec.wrap && Math.abs(value - prev) > WRAP_GAP);
          d += `${lift ? (d === '' ? 'M' : ' M') : ' L'} ${x(i).toFixed(1)} ${y(value).toFixed(1)}`;
          prev = value;
        }
        if (d.trim() === '') return '';
        return `<path class="series series-${spec.id}" data-series="${spec.id}"
            d="${d.trim()}" fill="none" stroke="${spec.colour}" stroke-width="1"
            ${spec.dash ? `stroke-dasharray="${spec.dash}"` : ''}
            vector-effect="non-scaling-stroke" opacity="${spec.opacity || 1}" />`;
      })
      .join('');

    const pips = this.pipRows()
      .map((p) => {
        const px = x(p.index).toFixed(1);
        const colour = SEVERITY_COLOUR[p.severity] || SEVERITY_COLOUR.info;
        return `<line x1="${px}" y1="${padTop}" x2="${px}" y2="${padTop + plotHeight}"
                  stroke="${colour}" stroke-width="1" opacity="0.5" />
                <circle cx="${px}" cy="${padTop + plotHeight + 4}" r="2.5" fill="${colour}" />`;
      })
      .join('');

    // Vertical key: swatch, label, and a value that tracks the slider. The whole
    // row is the toggle, so a series can be switched off from the same control
    // that reads its value.
    const keys = specs
      .map(
        (spec) => `<button type="button" class="strip-key" data-toggle="${spec.id}" aria-pressed="true"
            title="Show or hide ${spec.label}">
            <span class="swatch" style="background:${spec.colour}"></span>
            <span class="k-label">${spec.label}${spec.unit ? ` (${spec.unit})` : ''}</span>
            <span class="k-value num" data-value="${spec.id}">—</span>
          </button>`
      )
      .join('');
    const note = this.axisNote ? this.axisNote() : '';

    this.container.innerHTML = `
      <div class="strip-left">${this.leftExtras()}</div>
      <div class="strip-track ${this.trackClass ? this.trackClass() : ''}" tabindex="0" role="slider"
           aria-label="${this.ariaLabel()}" aria-valuemin="0" aria-valuemax="${count - 1}" aria-valuenow="${this.index}">
        <svg viewBox="0 0 ${width} ${height}" preserveAspectRatio="none" aria-hidden="true">
          ${this.underlay ? this.underlay(geom, width) : ''}
          ${paths}
          ${pips}
          <line class="playhead" x1="0" y1="0" x2="0" y2="${height}"
                stroke="var(--text)" stroke-width="1" vector-effect="non-scaling-stroke" />
        </svg>
      </div>
      <div class="strip-keys">${keys}${note ? `<span class="axis-note">${note}</span>` : ''}</div>`;

    this.track = this.container.querySelector('.strip-track');
    this.playhead = this.container.querySelector('.playhead');
    this._width = width;
    this._duration = duration;
    this._specs = specs;

    this.bind();
    this.bindExtras();
    this.applyVisibility();
    this.setIndex(this.index);
  }

  bind() {
    const seekFrom = (event) => {
      const rect = this.track.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
      this.onSeek(this.flight.indexAtTime(ratio * this._duration));
    };
    this.track.addEventListener('pointerdown', (event) => {
      this._dragging = true;
      this.track.setPointerCapture(event.pointerId);
      seekFrom(event);
    });
    this.track.addEventListener('pointermove', (event) => { if (this._dragging) seekFrom(event); });
    const release = () => { this._dragging = false; };
    this.track.addEventListener('pointerup', release);
    this.track.addEventListener('pointercancel', release);

    for (const button of this.container.querySelectorAll('[data-toggle]')) {
      button.addEventListener('click', () => this.toggleSeries(button.dataset.toggle));
    }
  }

  /** Move the marker and refresh the key values for this row. */
  setIndex(index) {
    this.index = index;
    if (!this.playhead) return;
    const t = this.flight.cols.timeS[index] ?? 0;
    const px = (t / this._duration) * this._width;
    this.playhead.setAttribute('x1', px);
    this.playhead.setAttribute('x2', px);
    if (this.track) this.track.setAttribute('aria-valuenow', index);
    for (const spec of this._specs || []) {
      const node = this.container.querySelector(`[data-value="${spec.id}"]`);
      if (node) node.textContent = this.fmtVal(spec, this.flight.cols[spec.key][index]);
    }
  }

  destroy() {
    this.container.innerHTML = '';
  }
}
